// The Google dashboard's top filter bar — the same six-facet shape as the Meta
// reporting page (Brand / Industry / AM / Group / Type / Status), plus currency,
// bid strategy and per-level name-contains boxes.
//
// Account-level facets (Brand / Group / Industry / AM / Account) resolve through
// ATTRIBUTION server-side, so filtering by Brand on a SHARED Google customer id
// shows only that brand's campaigns rather than the whole shared account.

import { useMemo } from 'react';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { Button } from '../../components/ui/button';
import { TokenChipInput } from '../../components/ui/TokenChipInput';
import { DateRangePicker } from '../reporting/DateRangePicker';
import { formatEnum } from './metricFormat';
import { hasNameFilters } from '../../lib/reportingNameFilters';
import type {
  GoogleAdsFilterOptions,
  GoogleAdsFilters,
} from '../../types/googleAdsReporting';
import type { ReportingDatePreset } from '../../types/reporting';
import type { DateRange } from '../../lib/reportingDateRange';

/** The three name boxes, shallowest → deepest (the canonical tree order). */
const NAME_FILTER_INPUTS = [
  { key: 'campaignName', label: 'Campaign' },
  { key: 'adGroupName', label: 'Ad group' },
  { key: 'adName', label: 'Ad' },
] as const;

interface Props {
  filters: GoogleAdsFilters;
  options: GoogleAdsFilterOptions | undefined;
  preset: ReportingDatePreset;
  onPresetChange: (preset: ReportingDatePreset) => void;
  custom: DateRange;
  onCustomChange: (range: DateRange) => void;
  onChange: (patch: Partial<GoogleAdsFilters>) => void;
  onReset: () => void;
}

/** Distinct {value,label} options, sorted by label. */
function facet(
  values: Array<{ id: string | null; name: string | null }>,
): Array<{ value: string; label: string }> {
  const byId = new Map<string, string>();
  for (const v of values) {
    if (v.id == null) continue;
    if (!byId.has(v.id)) byId.set(v.id, v.name ?? v.id);
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function GoogleAdsFilterBar({
  filters,
  options,
  preset,
  onPresetChange,
  custom,
  onCustomChange,
  onChange,
  onReset,
}: Props): React.JSX.Element {
  const accounts = options?.accounts ?? [];

  const brandOptions = useMemo(
    () => facet(accounts.map((a) => ({ id: a.brandAccountId, name: a.brandName }))),
    [accounts],
  );
  const groupOptions = useMemo(
    () => facet(accounts.map((a) => ({ id: a.groupAccountId, name: a.groupName }))),
    [accounts],
  );
  const industryOptions = useMemo(
    () => facet(accounts.map((a) => ({ id: a.industry, name: formatEnum(a.industry) }))),
    [accounts],
  );
  const amOptions = useMemo(
    () => facet(accounts.map((a) => ({ id: a.accountManagerId, name: a.accountManagerName }))),
    [accounts],
  );
  const accountOptions = useMemo(
    () => facet(accounts.map((a) => ({ id: a.accountId, name: a.accountName }))),
    [accounts],
  );
  // Country is its own label — the roster stores the display name ("Australia"),
  // so the id and the label are the same value, exactly as on the Meta bar.
  const countryOptions = useMemo(
    () => facet(accounts.map((a) => ({ id: a.country, name: a.country }))),
    [accounts],
  );
  const typeOptions = useMemo(
    () => (options?.channelTypes ?? []).map((t) => ({ value: t, label: formatEnum(t) })),
    [options?.channelTypes],
  );
  const statusOptions = useMemo(
    () => (options?.campaignStatuses ?? []).map((s) => ({ value: s, label: formatEnum(s) })),
    [options?.campaignStatuses],
  );
  const bidOptions = useMemo(
    () => (options?.biddingStrategyTypes ?? []).map((s) => ({ value: s, label: formatEnum(s) })),
    [options?.biddingStrategyTypes],
  );

  const anyActive =
    (filters.brandAccountIds?.length ?? 0) > 0 ||
    (filters.groupAccountIds?.length ?? 0) > 0 ||
    (filters.industries?.length ?? 0) > 0 ||
    (filters.accountManagerIds?.length ?? 0) > 0 ||
    (filters.accountIds?.length ?? 0) > 0 ||
    (filters.countries?.length ?? 0) > 0 ||
    (filters.channelTypes?.length ?? 0) > 0 ||
    (filters.campaignStatuses?.length ?? 0) > 0 ||
    (filters.biddingStrategyTypes?.length ?? 0) > 0 ||
    (filters.currencies?.length ?? 0) > 0 ||
    hasNameFilters(filters.nameFilters);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
      <DateRangePicker
        preset={preset}
        onPresetChange={onPresetChange}
        custom={custom}
        onCustomChange={onCustomChange}
      />
      <MultiSelectFilter
        label="Brand"
        options={brandOptions}
        selected={filters.brandAccountIds ?? []}
        onChange={(v) => onChange({ brandAccountIds: v })}
      />
      <MultiSelectFilter
        label="Industry"
        options={industryOptions}
        selected={filters.industries ?? []}
        onChange={(v) => onChange({ industries: v })}
      />
      <MultiSelectFilter
        label="Account manager"
        options={amOptions}
        selected={filters.accountManagerIds ?? []}
        onChange={(v) => onChange({ accountManagerIds: v })}
      />
      <MultiSelectFilter
        label="Group"
        options={groupOptions}
        selected={filters.groupAccountIds ?? []}
        onChange={(v) => onChange({ groupAccountIds: v })}
      />
      <MultiSelectFilter
        label="Account"
        options={accountOptions}
        selected={filters.accountIds ?? []}
        onChange={(v) => onChange({ accountIds: v })}
      />
      {/* Country — an account-level facet like Brand / Group / Industry / AM, so
          it resolves through attribution server-side rather than off the Google
          customer. Sits beside them, and matches the Meta bar's "Country". */}
      <MultiSelectFilter
        label="Country"
        options={countryOptions}
        selected={filters.countries ?? []}
        onChange={(v) => onChange({ countries: v })}
      />
      <MultiSelectFilter
        label="Campaign type"
        options={typeOptions}
        selected={filters.channelTypes ?? []}
        onChange={(v) => onChange({ channelTypes: v })}
      />
      <MultiSelectFilter
        label="Status"
        options={statusOptions}
        selected={filters.campaignStatuses ?? []}
        onChange={(v) => onChange({ campaignStatuses: v })}
      />
      <MultiSelectFilter
        label="Bid strategy"
        options={bidOptions}
        selected={filters.biddingStrategyTypes ?? []}
        onChange={(v) => onChange({ biddingStrategyTypes: v })}
      />
      {/* Per-level "name contains" — independent CHIP boxes. Tokens OR within a
          box (several spellings of one thing), boxes AND with each other (three
          separate questions), so a user can narrow to "$6 for 6 weeks" campaigns
          AND "Men" ad groups AND "Hero" ads. A filter on a level the tree isn't
          showing still narrows: the entity survives only if it has a descendant
          matching every active box. */}
      {NAME_FILTER_INPUTS.map(({ key, label }) => (
        <div key={key} className="w-56">
          <TokenChipInput
            value={filters.nameFilters?.[key] ?? []}
            onChange={(tokens) =>
              onChange({ nameFilters: { ...filters.nameFilters, [key]: tokens } })
            }
            placeholder={`${label} contains…`}
            ariaLabel={`${label} name contains`}
          />
        </div>
      ))}
      {anyActive && (
        <Button variant="ghost" size="sm" onClick={onReset}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
